import { ConvexProviderWithAuth, ConvexReactClient } from 'convex/react'
import { useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import type { Locale } from '../lib/i18n'
import { ANONYMOUS_AUTH, AppAuthProvider } from '../lib/auth/app-auth'
// Server: the real runtime, statically. Browser: a registry the client entry
// fills BEFORE hydration, only when the server rendered this page with Clerk
// (`<html data-clerk="1">`). See src/lib/auth/clerk-mount.ts.
import { getClerkRuntime } from './clerk-runtime-source'

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const convexUrl = import.meta.env.VITE_CONVEX_URL

const ANONYMOUS_CONVEX_AUTH = {
  isLoading: false,
  isAuthenticated: false,
  fetchAccessToken: () => Promise.resolve(null),
}

/**
 * Convex auth for visitors without Clerk: settled + unauthenticated, so
 * `useConvexAuth()` works everywhere and public functions run immediately.
 */
function useAnonymousConvexAuth() {
  return ANONYMOUS_CONVEX_AUTH
}

/**
 * App-wide providers.
 *
 * `clerk` is decided once per document by the server (auth screens + visitors
 * with a Clerk session hint) and is stable for the page's lifetime, so the two
 * branches below never swap at runtime:
 *   - Clerk pages: Clerk + Convex-with-Clerk (lazy chunk, preloaded before
 *     hydration) bridge their live state into the app auth facade.
 *   - Everyone else: the instant anonymous auth state + a plain Convex client
 *     (public queries/mutations such as early-access Pro lessons, newsletter,
 *     waitlist). Convex opens its socket lazily, on the first query.
 */
export function Providers({
  locale,
  clerk,
  children,
}: {
  locale: Locale
  clerk: boolean
  children: ReactNode
}) {
  const convex = useMemo(() => {
    if (!convexUrl || !convexUrl.startsWith('https://')) return null
    return new ConvexReactClient(convexUrl)
  }, [])

  const ClerkRuntime = clerk && clerkPublishableKey ? getClerkRuntime() : null

  // Safety net: a client-side navigation into a Clerk page from an anonymous
  // document (the app uses plain links, so this should not happen) reloads
  // the document so the server can mount Clerk properly.
  const missingRuntime = clerk && Boolean(clerkPublishableKey) && !ClerkRuntime
  useEffect(() => {
    if (missingRuntime) window.location.reload()
  }, [missingRuntime])

  if (ClerkRuntime && clerkPublishableKey) {
    return (
      <ClerkRuntime
        publishableKey={clerkPublishableKey}
        locale={locale}
        convex={convex}
      >
        {children}
      </ClerkRuntime>
    )
  }

  return (
    <AppAuthProvider value={ANONYMOUS_AUTH}>
      {convex ? (
        <ConvexProviderWithAuth
          client={convex}
          useAuth={useAnonymousConvexAuth}
        >
          {children}
        </ConvexProviderWithAuth>
      ) : (
        children
      )}
    </AppAuthProvider>
  )
}
